import { Button, Form, Input, InputNumber, Modal, Select, Skeleton, Spin } from 'antd';
import './index.less';
import { useEffect, useState, useRef, useContext } from 'react';
import MonixModal from '@/global/components/monix-modal';
import { ReactComponent as CloseSvg } from './icons/close.svg';
import { ReactComponent as AddSvg } from './icons/add.svg';
import classNames from "classnames";
import { ReactComponent as CheckSvg } from '@/global/icons/table/check.svg';
import { ReactComponent as CheckedSvg } from '@/global/icons/table/checked.svg';
import { GlobalMonitorParameter } from '@/global/interface/lib-device/global-monitor-parameter';
import { GlobalSensor } from '@/global/interface/lib-device/global-sensor';
import { getGlobalMonitorParameters } from '@/api/lib/lib-monitor-parameter';
import { MONITOR_PARAMETER_TYPE } from '@/enums/monitor-parameter-type';
import { useParams } from 'react-router-dom';
import TnMessage from '@/global/components/message';
import { createGlobalSensor, getGlobalSensors } from '@/api/lib/lib-sensor';
import { createProjectGSensors, deleteProjectGSensor, getProjectSensors } from '@/api/project-device/project-sensor';
import axios from 'axios';
import { RsProjectGlobalSensor } from '@/global/interface/project-device/project-sensor';
import { SideContext } from '@/App';
import { SideStatus } from '@/global/store/side-store';
import { SENSOR_TYPE_LIST } from '@/enums/sensor-type';
import { SIGNAL_TYPE_LIST } from '@/enums/signal-type';
import { VERIFY_TYPE_LIST } from '@/enums/verify-type';

interface IProps {
    visible: boolean,
    setVisible: (e) => void,
}

export default function AddSensorMarkingModal(props: IProps) {
    const { projectId } = useParams();
    const sideContext = useContext<SideStatus>(SideContext);
    const [globalParams, setGlobalParams] = useState<GlobalMonitorParameter[]>([]); // 全局直接监测参数
    const [globalSensors, setGlobalSensors] = useState<GlobalSensor[]>([]); // 全局传感器
    const [projectSensors, setProjectSensors] = useState<GlobalSensor[]>([]); // 项目引用的传感器
    const [displaySensors, setDisplaySensors] = useState<GlobalSensor[]>([]); // 展示的传感器
    const [leftLoading, setLeftLoading] = useState<boolean>(false); // 左边的loading
    const [rightLoading, setRightLoading] = useState<boolean>(false); // 右边的loading
    const [activeItem, setActiveItem] = useState<GlobalMonitorParameter>(null); // 选中的条目
    const addBtnRef = useRef(null); // 添加表单的ref
    const [addForm] = Form.useForm(); // 添加表单
    const [showAddModel, setShowAddModel] = useState<boolean>(false) // 添加弹窗标志
    const [addLoading, setAddLoading] = useState<boolean>(false) // 添加弹窗loading

    useEffect(() => {
        if (!projectId) return;
        generateData();
    }, [projectId])

    useEffect(() => {
        if (!showAddModel) {
            addForm.resetFields();
        } else {
            addForm.setFieldValue("globalParameterIds", [activeItem?.id])
        }
    }, [showAddModel])

    // 获取详情
    useEffect(() => {
        if (!globalSensors || !projectSensors || !activeItem) {
            setDisplaySensors([]);
            return;
        }
        // 第一步：把库中和这个直接参数相关的列出来
        // 第二部：把项目中引用的勾选出来
        let display = globalSensors?.filter(item => item?.globalSensorParameters?.some(gsp => gsp.globalMonitorParameterId === activeItem?.id)) || [];
        setDisplaySensors(display);
    }, [globalSensors, projectSensors, activeItem])

    // 获取数据
    const generateData = (reSelect = true) => {
        setLeftLoading(true);
        setRightLoading(true);
        axios.all([getGlobalMonitorParameters(), getGlobalSensors(), getProjectSensors(+projectId, null)]).then(res => {
            let data1 = res[0].data || [];
            let data2 = res[1].data || [];
            let data3 = res[2].data || [];
            data1 = data1?.filter(item => item.type === MONITOR_PARAMETER_TYPE.DIRECT) || [];
            setGlobalParams(data1);
            if (reSelect && data1?.length > 0) {
                setActiveItem(data1[0]);
            }
            setGlobalSensors(data2);
            setProjectSensors(data3);
        }).catch(err => {
            TnMessage.error("获取数据失败");
        }).finally(() => {
            setLeftLoading(false);
            setRightLoading(false);
        })
    }

    // 点击详情的checkbox
    const boxClick = (globalSensor: GlobalSensor, selected: boolean) => {
        if (!globalSensor) return;
        if (selected) {
            // 已选中，解绑
            setRightLoading(true);
            deleteProjectGSensor(+projectId, globalSensor?.id).then(res => {
                if (res.status === 200) {
                    TnMessage.success("编辑成功");
                    let idx = projectSensors?.findIndex(item => item.id === globalSensor?.id);
                    projectSensors.splice(idx, 1);
                    setProjectSensors([...projectSensors]);
                    sideContext.needRenderSensorSide();
                }
            }).catch(err => {
                TnMessage.error("编辑失败");
            }).finally(() => {
                setRightLoading(false);
            })
        } else {
            // 未选中，添加
            let par: RsProjectGlobalSensor[] = [];
            par.push({
                projectId: +projectId,
                globalSensorId: globalSensor?.id,
            })
            setRightLoading(true);
            createProjectGSensors(par).then(res => {
                if (res.status === 200) {
                    TnMessage.success("编辑成功");
                    sideContext.needRenderSensorSide();
                    projectSensors.push(globalSensor);
                    setProjectSensors([...projectSensors]);
                }
            }).catch(err => {
                TnMessage.error("编辑失败");
            }).finally(() => {
                setRightLoading(false);
            })
        }
    }

    // 点击添加弹窗确认按钮
    const onAddSaveClick = () => {
        if (addLoading) return;
        if (addBtnRef.current) {
            addBtnRef.current.click();
        }
    }

    // 创建表单提交
    const onAddFinish = (values) => {
        setAddLoading(true);
        let data = { ...values };
        createGlobalSensor(data).then(res => {
            if (res.status === 200) {
                generateData(false);
                TnMessage.success("添加成功");
                setShowAddModel(false);
            }
        }).catch(err => {
            TnMessage.error(err.data || '添加失败');
        }).finally(() => {
            setAddLoading(false);
        });
    }

    return (
        <>
            <Modal
                open={props.visible}
                onCancel={() => { props.setVisible(false) }}
                footer={null}
                closable={false}
                wrapClassName={`add-sensor-marking-modal`}
                mask
                destroyOnClose
                width={812}
            >
                <div className='marking-body'>
                    <div className='title'>
                        <div className='name'>传感器选型</div>
                        <div className='icon' onClick={() => { props.setVisible(false) }}><CloseSvg /></div>
                    </div>
                    <div className='main'>
                        <div className="table-area">
                            <div className="head-row"><div className="head-cell">直接参数</div></div>
                            <Skeleton loading={leftLoading} active>
                                {globalParams?.map((item, key) => (
                                    <div className="body-row" key={key} onClick={() => setActiveItem(item)}>
                                        <div className={classNames({
                                            "body-cell": true,
                                            "body-cell-active": activeItem?.id === item.id,
                                        })}>{item?.name || ''}</div>
                                    </div>
                                ))}
                            </Skeleton>
                        </div>
                        <div className="detail-area">
                            <div className="head-row"><div className="head-cell">传感器型号</div></div>
                            <Skeleton loading={rightLoading} active>
                                <div className='detail-panel'>
                                    {displaySensors?.map((item, key) => (
                                        <div className="detail-row" key={key}>
                                            {!projectSensors?.some(ps => ps?.id === item?.id) && <CheckSvg onClick={() => boxClick(item, false)} />}
                                            {projectSensors?.some(ps => ps?.id === item?.id) && <CheckedSvg onClick={() => boxClick(item, true)} />}
                                            {item?.marking || ''}
                                        </div>
                                    ))}
                                    <div className="detail-row"><AddSvg onClick={() => setShowAddModel(true)} /></div>
                                </div>
                            </Skeleton>
                        </div>
                    </div>
                    {/* <div className='button-area'>
                        <div className={classNames({
                            "cancel": true,
                            "cancel-disable": false
                        })} onClick={onCancelClick}>{"取消"}</div>
                        <div className={classNames({
                            "save": true,
                            "save-disable": false
                        })} onClick={onSaveClick}>{"确认"}</div>
                    </div> */}
                </div>
            </Modal>

            <MonixModal
                title={"创建设备"}
                visible={showAddModel}
                setVisible={(e) => setShowAddModel(e)}
                onSaveClick={onAddSaveClick}
                wrapClassName="form-modal"
                saveDisable={addLoading}
            >
                <Spin wrapperClassName="form-body" spinning={addLoading}>
                    <Form
                        colon={false}
                        form={addForm}
                        autoComplete="off"
                        labelCol={{ span: 6 }}
                        onFinish={onAddFinish}
                        initialValues={{
                            globalParameterIds: [activeItem?.id]
                        }}
                    >
                        <Form.Item label="传感器类型:" name="type" rules={[{ required: true, message: '传感器类型不能为空' }]}>
                            <Select>
                                {SENSOR_TYPE_LIST.map(item => (
                                    <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item label="名称:" name="name" rules={[{ required: true, message: '名称不能为空' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="厂商:" name="manufacturer" rules={[{ required: true, message: '厂商不能为空' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="型号:" name="marking" rules={[{ required: true, message: '型号不能为空' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="精度:" name="precision" rules={[{ required: true, message: '精度不能为空' }]}>
                            <InputNumber style={{ width: '100%' }} min={0} precision={0} />
                        </Form.Item>
                        <Form.Item label="量程:" name="range" rules={[{ required: true, message: '量程不能为空' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="信号类型:" name="signalType" rules={[{ required: true, message: '信号类型不能为空' }]}>
                            <Select>
                                {SIGNAL_TYPE_LIST.map(item => (
                                    <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item label="验证类型:" name="verifyType">
                            <Select>
                                {VERIFY_TYPE_LIST.map(item => (
                                    <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item label="英文名称:" name="englishName">
                            <Input />
                        </Form.Item>
                        <Form.Item label="计算公式:" name="formulaStr">
                            <Input />
                        </Form.Item>
                        <Form.Item label="监测参数:" name="globalParameterIds">
                            <Select mode="tags">
                                {globalParams?.map(item => (
                                    <Select.Option key={item.id} value={item.id}>{item?.name || ''}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Button ref={addBtnRef} htmlType="submit" style={{ display: 'none' }}></Button>
                    </Form>
                </Spin>
            </MonixModal>
        </>

    )
}